'use client';
/* ── DÉSTOCKER UN ALIQUOT — la fenêtre de la commande flottante ────────────
 *
 * Le geste quotidien (RQ, 03/10/2026) : on sort un tube du congélateur, on le
 * scanne, on voit tout de suite le résultat, en grand, lisible à un mètre.
 *
 *  · UN SEUL CHAMP, qui a le focus à l'ouverture et le GARDE après chaque
 *    déstockage réussi : on enchaîne les tubes sans toucher la souris.
 *  · Il accepte indifféremment la DOUCHETTE (qui tape le code puis Entrée) et
 *    le CLAVIER, qui en fait une barre de recherche (identifiant, produit, lot,
 *    niveau). Un code complet déstocke ; une saisie partielle propose des
 *    aliquots à choisir — c'est le déstockage MANUEL (étiquette illisible,
 *    douchette en panne, tube historique sans étiquette), noté « sans scan »
 *    au journal.
 *  · On scanne D'ABORD : l'outil ne commence pas par proposer. S'il existe un
 *    aliquot plus ancien encore valide, une ALERTE le désigne ; on va le
 *    chercher, ou l'on confirme le tube tenu avec un motif. Aucun plus ancien :
 *    rien à cliquer.
 *
 * Les règles sont au moteur (`backend/src/cqi-aliquots.ts`) ; cette fenêtre
 * n'en recalcule aucune. */
import React, { useEffect, useRef, useState } from 'react';
import { Modale, inputStyle } from '@/components/ui';
import { fetchEcriture } from '@/lib/ecrire';
import { fmtDate } from '@/lib/meta';
import { niveauCourt, instantLabo } from '@/lib/aliquots-affichage';
import { ressembleACode, type AliquotVue, type LotVue, type ReponseDestockage, type ReponseRecherche } from '@/components/aliquots-types';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { styleBouton } from '@/shared/boutonStyles';
/** Un tube de la session. `annule` : remis au congélateur — la ligne RESTE,
 *  marquée, pour qu'on voie ce qui a été fait (RQ, 03/10/2026 : la liste
 *  disparaissait après une annulation, sans un mot). */
type Fait = { aliquot: AliquotVue; mode: 'scan' | 'manuel'; le: number; annule?: boolean };
const cleFait = (f: Fait) => `${f.aliquot.documentId}-${f.le}`;
/** La règle du moteur (`motifValide`) : 5 caractères utiles au moins. */
const MOTIF_MIN = 5;

/** Un bouton désactivé doit SE VOIR désactivé (le curseur seul ne suffit pas)
 *  et la raison s'écrire à côté — le piège du « bouton grisé sans phrase ». */

const TEINTES = {
  ok: { fond: 'var(--green-50, #ecfdf3)', bord: 'var(--green-600, #16a34a)', encre: 'var(--green-700, #15803d)' },
  alerte: { fond: 'var(--amber-50, #fffbeb)', bord: 'var(--amber-500, #f59e0b)', encre: 'var(--amber-800, #92400e)' },
  refus: { fond: 'var(--red-50, #fef2f2)', bord: 'var(--red-600, #dc2626)', encre: 'var(--red-700, #b91c1c)' },
};

/** Ce qu'on demande au moteur : un code SCANNÉ, ou un LOT choisi à la main.
 *  ⚠️ Plus de `documentId` de tube (11/10/2026) : l'écran ne désigne plus un
 *  tube, c'est le moteur qui en prend un dans le lot (`tubeAPrendre`), par la
 *  même règle que le scan — sinon le même lot donnerait deux tubes différents
 *  selon le geste. */
type Demande = { mode: 'scan'; saisie: string } | { mode: 'manuel'; flaconRef: string };

export function DestockageFenetre({ onClose }: { onClose: () => void }) {
  const champ = useRef<HTMLInputElement | null>(null);
  const [saisie, setSaisie] = useState('');
  /* Les suggestions sont des LOTS depuis le 11/10/2026 — une ligne par boîte,
     avec ce qu'il en reste. Voir `ReponseRecherche`. */
  const [suggestions, setSuggestions] = useState<(LotVue & { aPrendreDabord: boolean })[]>([]);
  const [cherche, setCherche] = useState(false);
  const [reponse, setReponse] = useState<ReponseDestockage | null>(null);
  /* Ce qu'il faut renvoyer si l'on confirme malgré l'alerte : le même tube,
     désigné de la même façon. */
  const [enAttente, setEnAttente] = useState<Demande | null>(null);
  const [motif, setMotif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [faits, setFaits] = useState<Fait[]>([]);
  const [horsCongelateur, setHorsCongelateur] = useState<ReponseRecherche['horsCongelateur']>(null);
  /* L'annulation se saisit DANS la fenêtre, sous le tube (RQ, 03/10/2026) :
     la boîte native du navigateur figeait la page et ne suivait pas l'outil. */
  const [annulation, setAnnulation] = useState<{ cle: string; motif: string; erreur: string; envoi: boolean } | null>(null);
  const [confirmation, setConfirmation] = useState('');

  const refocaliser = () => window.setTimeout(() => champ.current?.focus(), 0);
  useEffect(() => { refocaliser(); }, []);

  /* Suggestions au fil de la frappe, à partir de deux caractères et quand la
     saisie ne ressemble pas déjà à un code complet. */
  useEffect(() => {
    const q = saisie.trim();
    // Saisie trop courte ou code complet : rien à chercher. Les suggestions
    // périmées sont masquées au rendu (`visibles`), pas effacées ici.
    if (q.length < 2 || ressembleACode(q)) return undefined;
    let vivant = true;
    const minuterie = window.setTimeout(() => {
      setCherche(true);
      void fetch(`/api/ged/ged/aliquots/recherche?q=${encodeURIComponent(q)}`, { credentials: 'include' })
        .then((r) => (r.ok ? r.json() : { resultats: [], horsCongelateur: null }))
        .then((d: ReponseRecherche) => { if (vivant) { setSuggestions(d.resultats || []); setHorsCongelateur(d.horsCongelateur || null); } })
        .catch(() => { if (vivant) { setSuggestions([]); setHorsCongelateur(null); } })
        .finally(() => { if (vivant) setCherche(false); });
    }, 220);
    return () => { vivant = false; window.clearTimeout(minuterie); };
  }, [saisie]);

  const destocker = async (d: Demande, avecMotif = '') => {
    setEnvoi(true); setConfirmation('');
    try {
      const r = await fetchEcriture('/api/ged/ged/aliquots/destocker', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...d, motif: avecMotif || undefined }),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok || !data) {
        setReponse({ issue: 'inconnu', raison: data?.error?.message || 'Le déstockage n’a pas pu être enregistré.' });
        return;
      }
      const rep = data as ReponseDestockage;
      setReponse(rep);
      if (rep.issue === 'plus_ancien') { setEnAttente(d); return; }
      setEnAttente(null); setMotif('');
      if (rep.issue === 'ok') {
        setFaits((f) => [{ aliquot: rep.aliquot, mode: d.mode, le: Date.now() }, ...f].slice(0, 12));
        setSaisie(''); setSuggestions([]);
      }
    } finally {
      setEnvoi(false);
      refocaliser();
    }
  };

  const valider = () => {
    const q = saisie.trim();
    if (!q || envoi) return;
    if (ressembleACode(q)) { void destocker({ mode: 'scan', saisie: q }); return; }
    // Une seule suggestion : Entrée la prend. Plusieurs : il faut choisir.
    if (suggestions.length === 1 && suggestions[0]) {
      void destocker({ mode: 'manuel', flaconRef: suggestions[0].flaconRef });
    }
  };

  const annuler = async (f: Fait) => {
    const a = annulation;
    if (!a || a.cle !== cleFait(f) || a.motif.trim().length < MOTIF_MIN || a.envoi) return;
    setAnnulation({ ...a, envoi: true, erreur: '' });
    const r = await fetchEcriture('/api/ged/ged/aliquots/annuler-destockage', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ documentId: f.aliquot.documentId, motif: a.motif.trim() }),
    }).catch(() => null);
    const data = r ? await r.json().catch(() => null) : null;
    if (!r || !r.ok) {
      // Le refus reste SOUS LE TUBE, là où l'on regarde (15 minutes passées,
      // pas la même personne…).
      setAnnulation({ ...a, envoi: false, erreur: data?.error?.message || 'Annulation refusée.' });
      return;
    }
    setFaits((x) => x.map((y) => (y === f ? { ...y, annule: true, aliquot: data?.aliquot || y.aliquot } : y)));
    setAnnulation(null);
    setReponse(null);
    setConfirmation(`Annulé — le tube de ${f.aliquot.produit} est de retour au congélateur.`);
    refocaliser();
  };

  const q = saisie.trim();
  const visibles = q.length >= 2 && !ressembleACode(q) ? suggestions : [];
  const motifCourt = motif.trim().length < MOTIF_MIN;
  const t = reponse?.issue === 'ok' ? TEINTES.ok : reponse?.issue === 'plus_ancien' ? TEINTES.alerte : TEINTES.refus;

  return (
    <Modale titre="Déstocker un aliquot" largeur={640} sansSaisie onClose={onClose}
      sousTitre="Scannez le tube à la sortie du congélateur — ou tapez pour chercher (identifiant, produit, lot, niveau).">
      <div style={{ display: 'grid', gap: 14 }}>
        <input ref={champ} value={saisie} aria-label="Code de l’aliquot ou recherche"
          placeholder="Scanner… ou chercher « lypho 2 »"
          autoComplete="off" spellCheck={false}
          onChange={(e) => { setSaisie(e.target.value); setConfirmation(''); setHorsCongelateur(null); if (reponse?.issue !== 'plus_ancien') setReponse(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); valider(); } }}
          style={{ ...inputStyle, height: 52, fontSize: 22, fontFamily: 'var(--font-mono)', letterSpacing: 1 }} />

        {visibles.length > 0 && !reponse && (
          <div role="listbox" aria-label="Aliquots trouvés" style={{ display: 'grid', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
            {/* Dans l'ORDRE DE PRISE, servi par le moteur : le premier à sortir
                en tête, et désigné comme tel (RQ, 03/10/2026). */}
            {/* ⚠️ UNE LIGNE PAR LOT, AVEC CE QU'IL EN RESTE (RQ, 11/10/2026).
                La liste montrait un bouton par tube — douze boutons identiques
                pour une même boîte, qui faisaient choisir là où il n'y a rien
                à choisir. C'est l'outil qui prend le tube, par la même règle
                que le scan (`tubeAPrendre`). La date de reconstitution tient
                la place du numéro : c'est elle qui distingue deux boîtes du
                même produit. */}
            {visibles.map((l) => (
              <button key={l.flaconRef} type="button" role="option" aria-selected="false" className="bio-btn bio-btn--contour"
                onClick={() => void destocker({ mode: 'manuel', flaconRef: l.flaconRef })}
                style={{
                  ...styleBouton('contour', 'sm'), justifyContent: 'flex-start', gap: 10, height: 'auto', padding: '7px 10px', textAlign: 'start',
                  ...(l.aPrendreDabord ? { borderColor: 'var(--green-600, #16a34a)', background: 'var(--green-50, #ecfdf3)' } : {}),
                }}>
                {l.aPrendreDabord && (
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--green-700, #15803d)', textTransform: 'uppercase', letterSpacing: 0.3, whiteSpace: 'nowrap', flexShrink: 0 }}>à prendre d’abord</span>
                )}
                <span><strong>{l.produit}</strong> · {niveauCourt(l.niveau)} · lot {l.numeroLot || '—'} · reconstitué le {fmtDate(l.dateReconstitution)}</span>
                <span style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{l.restants} restant{l.restants > 1 ? 's' : ''}</span>
                <span style={{ marginInlineStart: 'auto', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  limite {l.limite ? fmtDate(l.limite) : 'inconnue'}
                </span>
              </button>
            ))}
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Choisir dans la liste = déstockage <strong>sans scan</strong>, consigné comme tel au journal.
            </span>
          </div>
        )}
        {cherche && !visibles.length && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Recherche…</span>}
        {/* Un tube tapé en entier qui n'est plus au congélateur : on dit
            POURQUOI il n'est pas dans la liste, plutôt qu'une liste vide. */}
        {!cherche && visibles.length === 0 && !reponse && horsCongelateur && saisie.trim().length >= 2 && !ressembleACode(saisie) && (
          <div role="status" style={{ fontSize: 13.5, color: TEINTES.refus.encre, background: TEINTES.refus.fond, borderRadius: 8, padding: '8px 12px' }}>
            Ce tube n’est pas au congélateur : {horsCongelateur.raison}
          </div>
        )}
        {!cherche && saisie.trim().length >= 2 && !ressembleACode(saisie) && !visibles.length && !reponse && !horsCongelateur && (
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun aliquot au congélateur ne correspond.</span>
        )}

        {confirmation && !reponse && (
          <div role="status" style={{ background: TEINTES.ok.fond, borderInlineStart: `6px solid ${TEINTES.ok.bord}`, borderRadius: 10, padding: '12px 16px', fontSize: 17, fontWeight: 700, color: TEINTES.ok.encre }}>
            {confirmation}
          </div>
        )}

        {reponse && (
          <div role={reponse.issue === 'ok' ? 'status' : 'alert'}
            style={{ background: t.fond, borderInlineStart: `6px solid ${t.bord}`, borderRadius: 10, padding: '16px 18px', display: 'grid', gap: 8 }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: t.encre, lineHeight: 1.2 }}>
              {reponse.issue === 'ok' ? 'Déstocké'
                : reponse.issue === 'plus_ancien' ? 'Il y a plus ancien'
                  : 'Refusé'}
            </div>
            {'aliquot' in reponse && (
              <div style={{ fontSize: 16, color: 'var(--text-strong)' }}>
                un tube de <strong>{reponse.aliquot.produit}</strong> · niveau <strong>{reponse.aliquot.niveau}</strong>
                {' '}· lot {reponse.aliquot.numeroLot || '—'} · reconstitué le {fmtDate(reponse.aliquot.dateReconstitution)}
              </div>
            )}
            {reponse.issue === 'ok' && (
              <div style={{ fontSize: 15, color: 'var(--text-body)' }}>
                {reponse.aliquot.limiteDecongele
                  ? <>À utiliser avant le <strong>{instantLabo(reponse.aliquot.limiteDecongele)}</strong>{reponse.aliquot.causeLibelle ? ` (${reponse.aliquot.causeLibelle})` : ''}.</>
                  : <>Limite après décongélation <strong>non calculable</strong> : {reponse.aliquot.manques.join(', ') || 'stabilité non renseignée'}.</>}
                {reponse.avertissements.map((a) => <div key={a} style={{ color: TEINTES.alerte.encre, marginTop: 4 }}>⚠ {a}</div>)}
              </div>
            )}
            {reponse.issue !== 'ok' && <div style={{ fontSize: 15, color: 'var(--text-body)', lineHeight: 1.45 }}>{reponse.raison}</div>}
            {reponse.issue === 'plus_ancien' && enAttente && (
              <div style={{ display: 'grid', gap: 8, marginTop: 4 }}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Bouton variante="primaire" taille="sm" type="button" onClick={() => { setReponse(null); setEnAttente(null); setMotif(''); setSaisie(''); refocaliser(); }}>
                    Je vais chercher le plus ancien
                  </Bouton>
                </div>
                <label htmlFor="destock-motif" style={{ fontSize: 13, fontWeight: 600 }}>Ou confirmer CE tube — motif obligatoire :</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input id="destock-motif" value={motif} onChange={(e) => setMotif(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !motifCourt) void destocker(enAttente, motif.trim()); }}
                    placeholder="ex. le plus ancien est introuvable au tiroir" aria-describedby="destock-motif-aide"
                    style={{ ...inputStyle, flex: 1 }} />
                  <Bouton disabled={motifCourt || envoi}
                    title={motifCourt ? 'Saisissez le motif pour confirmer ce tube' : undefined}
                    onClick={() => void destocker(enAttente, motif.trim())}>
                    Confirmer ce tube
                  </Bouton>
                </div>
                {motifCourt && (
                  <span id="destock-motif-aide" style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    Saisissez le motif pour confirmer ce tube ({MOTIF_MIN} caractères au moins).
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {faits.length > 0 && (
          <div style={{ display: 'grid', gap: 4 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-muted)' }}>Déstockés pendant cette session</div>
            {faits.map((f) => {
              const cle = cleFait(f);
              const ouverte = annulation?.cle === cle ? annulation : null;
              const court = !ouverte || ouverte.motif.trim().length < MOTIF_MIN;
              return (
                <div key={cle} style={{ display: 'grid', gap: 6, borderTop: '1px solid var(--border-subtle)', paddingTop: 4 }}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, ...(f.annule ? { color: 'var(--text-muted)' } : {}) }}>
                    <strong style={{ whiteSpace: 'nowrap', textDecoration: f.annule ? 'line-through' : undefined }}>{f.aliquot.produit} {niveauCourt(f.aliquot.niveau)}</strong>
                    <span>{f.aliquot.produit} · {niveauCourt(f.aliquot.niveau)}</span>
                    {f.mode === 'manuel' && <span style={{ fontSize: 12, color: 'var(--amber-700, #b45309)' }}>sans scan</span>}
                    {f.annule
                      ? <span style={{ marginInlineStart: 'auto', fontSize: 12, fontWeight: 700, color: 'var(--green-700, #15803d)' }}>annulé — de retour au congélateur</span>
                      : !ouverte && (
                        /* Proposé sans minuterie : passé 15 minutes, le moteur refuse
                           et dit pourquoi (le tube a pu dégeler). */
                        <Bouton taille="sm" type="button" onClick={() => { setConfirmation(''); setAnnulation({ cle, motif: '', erreur: '', envoi: false }); }} style={{ marginInlineStart: 'auto' }}>
                          Annuler (mauvais tube)
                        </Bouton>
                      )}
                  </div>
                  {ouverte && !f.annule && (
                    <div style={{ display: 'grid', gap: 6, background: 'var(--surface-sunken)', borderRadius: 8, padding: '8px 10px' }}>
                      <label htmlFor={`annul-${cle}`} style={{ fontSize: 12.5, fontWeight: 600 }}>
                        Annuler ce déstockage — le tube doit être resté au congélateur. Motif (consigné au journal) :
                      </label>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input id={`annul-${cle}`} autoFocus value={ouverte.motif} aria-describedby={`annul-aide-${cle}`}
                          placeholder="ex. mauvais tube scanné, remis aussitôt"
                          onChange={(e) => setAnnulation({ ...ouverte, motif: e.target.value, erreur: '' })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { e.preventDefault(); void annuler(f); }
                            if (e.key === 'Escape') { e.stopPropagation(); setAnnulation(null); refocaliser(); }
                          }}
                          style={{ ...inputStyle, flex: 1 }} />
                        <PiedActions taille="sm" annuler={{ libelle: 'Garder', onClick: () => { setAnnulation(null); refocaliser(); } }}
                          principale={{ libelle: 'Remettre au congélateur', libelleChargement: 'Remise…', chargement: ouverte.envoi, disabled: court || ouverte.envoi,
                            title: court ? 'Saisissez le motif pour annuler ce déstockage' : undefined, onClick: () => void annuler(f) }} />
                      </div>
                      {court && (
                        <span id={`annul-aide-${cle}`} style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          Saisissez le motif pour annuler ce déstockage ({MOTIF_MIN} caractères au moins).
                        </span>
                      )}
                      {ouverte.erreur && <span role="alert" style={{ fontSize: 12.5, color: TEINTES.refus.encre }}>{ouverte.erreur}</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Modale>
  );
}
